import { useState, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Lock,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ShieldCheck,
  ShieldAlert,
  Clock,
  CreditCard,
  FileText,
  Plus,
  RefreshCw,
  Smartphone,
  TrendingUp,
  Hash,
  Users,
  Wallet,
} from '@/lib/icons';

import {
  getCustomerById,
  getCustomerScoringStatus,
  checkCustomerEligibility,
  rescoreCustomer,
  updateCustomer,
} from '@/features/customers/api';
import { SCORING_POLL_INTERVAL_MS } from '@/features/customers/eligibilityPolling';
import { KycReviewCard } from '@/features/customers/components/KycReviewCard';
import { OdooLinkBadge } from '@/features/odoo/components/OdooLinkBadge';
import { KycReviewDialog } from '@/features/customers/components/KycReviewDialog';
import {
  CustomerEligibilityAssessment,
  invalidateEligibilityAssessment,
} from '@/features/eligibility';
import { getApplications } from '@/features/applications/api';
import { useFinancingGate } from '@/features/applications/financing-gate';
import { FinancingBlockInline } from '@/features/applications/components/FinancingBlockNotice';
import {
  INSTALLMENT_PERIOD_SUFFIX,
  installmentSummary,
} from '@/features/applications/application';
import { getContracts } from '@/features/contracts/api';
import { kycReviewState } from '@/features/customers/customer';
import type { KycReviewState } from '@/features/customers/customer';
import type { CreditTierInfoModel } from '@/features/customers/customer';
import { tierName, tierScoreRange } from '@/features/customers/tier';
import { useAppSelector } from '@/store/hooks';
import { formatCurrency, cn } from '@/lib/utils';
import { ButtonSpinner, SectionLoading } from '@/components/feedback';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContactHistoryPanel } from '@/features/feedback/components/ContactHistoryPanel';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/customers/$customerId')({
  component: CustomerDetailPage,
});

/* ── KYC Badge ──────────────────────────────────────────────── */
/**
 * Keyed on the four-state review model, not the three-label enum: `pending`
 * covers both "never sent us anything" and "sitting in our queue", and the
 * header badge is the first thing read on this page — it must agree with the
 * identity card in the sidebar rather than calling waiting work "Pending".
 */
const kycConfig: Record<
  KycReviewState,
  { label: string; className: string; icon: React.ReactNode }
> = {
  verified: {
    label: 'Verified',
    className:
      'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    icon: <ShieldCheck className="w-3 h-3 mr-1" />,
  },
  awaitingReview: {
    label: 'Awaiting review',
    className:
      'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    icon: <Clock className="w-3 h-3 mr-1" />,
  },
  notSubmitted: {
    label: 'Not submitted',
    className:
      'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    icon: <Clock className="w-3 h-3 mr-1" />,
  },
  rejected: {
    label: 'Sent back',
    className: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
    icon: <ShieldAlert className="w-3 h-3 mr-1" />,
  },
};

/* ── Credit Tier Metadata ───────────────────────────────────── */
const tierColors: Record<string, string> = {
  A: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30',
  B: 'text-blue-600 bg-blue-500/10 border-blue-500/30',
  C: 'text-amber-600 bg-amber-500/10 border-amber-500/30',
  D: 'text-orange-600 bg-orange-500/10 border-orange-500/30',
  E: 'text-red-600 bg-red-500/10 border-red-500/30',
};

// Explanatory copy only. Names and score ranges come from cs-engine's ladder
// — a local copy of those is what let this page drift from the backend.
const tierDescriptions: Record<string, string> = {
  A: 'Strongest credit profile. Qualifies for the highest financing limits and longest terms.',
  B: 'Good credit standing. Qualifies for most financing plans with standard terms.',
  C: 'Eligible for entry-level plans; smaller financing amounts.',
  D: 'Limited to smaller, shorter-term financing plans.',
  E: 'Thin credit history. Only the smallest, shortest plans are available.',
};

/* ── Application Status Badge ───────────────────────────────── */
function appStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    submitted: {
      label: 'Submitted',
      className: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
    },
    pending_kyc: {
      label: 'KYC Verified',
      className: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    },
    approved: {
      label: 'Approved',
      className: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    },
    rejected: {
      label: 'Rejected',
      className: 'bg-red-500/15 text-red-600 border-red-500/30',
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-gray-500/15 text-gray-600 border-gray-500/30',
    },
  };
  const cfg = map[status] ?? { label: status, className: '' };
  return <Badge className={cn('text-xs', cfg.className)}>{cfg.label}</Badge>;
}

/* ── Contract Status Badge ──────────────────────────────────── */
function contractStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    draft: {
      label: 'Draft',
      className: 'bg-gray-500/15 text-gray-600 border-gray-500/30',
    },
    pending_payment: {
      label: 'Pending Payment',
      className: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    },
    active: {
      label: 'Active',
      className: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    },
    completed: {
      label: 'Completed',
      className: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
    },
    defaulted: {
      label: 'Defaulted',
      className: 'bg-red-500/15 text-red-600 border-red-500/30',
    },
    cancelled: {
      label: 'Cancelled',
      className: 'bg-gray-500/15 text-gray-600 border-gray-500/30',
    },
  };
  const cfg = map[status] ?? { label: status, className: '' };
  return <Badge className={cn('text-xs', cfg.className)}>{cfg.label}</Badge>;
}

/* ── Main Page ──────────────────────────────────────────────── */
function CustomerDetailPage() {
  const { t } = useTranslation();
  const { customerId } = Route.useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Identity review lives in `KycReviewDialog`, which owns its own verdict
  // form and mutation. Only the open flag is held here, because two places
  // open it: the header action and the identity card in the sidebar.
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  /* ── Customer detail query ──────────────────────────────── */
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomerById(customerId),
    // Override the global ``refetchOnWindowFocus: false`` for this page —
    // agents commonly leave the detail tab open while doing work in
    // another tab (eligibility tools, ZAAD dashboards, paper-form scans).
    // Returning to the tab should always show the latest customer state.
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  /* ── Scoring status (polls while pending/in_progress) ───── */
  // Cheap cs-engine read; doesn't run the full eligibility pipeline.
  // Refetch every 15s while scoring is pending/in_progress, then stop.
  // A full scoring pass (CDR + ZaadCredit fetches) takes ~10s, and each
  // poll fans out into several backend calls, so 5s only added load.
  // ``refetchInterval`` returning false halts the timer.
  const { data: scoringStatusResult } = useQuery({
    queryKey: ['customer-scoring-status', customerId],
    queryFn: () => getCustomerScoringStatus(customerId),
    enabled: !!data?.value,
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      const status = query.state.data?.value?.status;
      return status === 'pending' || status === 'in_progress'
        ? SCORING_POLL_INTERVAL_MS
        : false;
    },
  });
  const scoringStatus = scoringStatusResult?.value?.status ?? null;
  const scoringFailureReason =
    scoringStatusResult?.value?.failureReason ?? null;

  /* ── Customer applications ──────────────────────────────── */
  const { data: appsData } = useQuery({
    queryKey: ['customer-applications', customerId],
    queryFn: () => getApplications({ pageSize: 50 }),
    enabled: !!data?.value,
  });

  /* ── Customer contracts ─────────────────────────────────── */
  const { data: contractsData } = useQuery({
    queryKey: ['customer-contracts', customerId],
    queryFn: () => getContracts(),
    enabled: !!data?.value,
  });

  // When the scoring poll lands a ``complete`` status, refresh the
  // customer record so the score + tier columns surface. cs-engine
  // pushed the values onto the offerings-service customer row via the
  // eligibility chain — we just need to reread.
  // The scorecard is reread only when a run actually ends here, not on every
  // mount of an already-scored customer: its telco block reports the
  // snapshots that run wrote, whatever verdict it reached.
  const msisdn = data?.value?.phoneNumber ?? null;
  const previousScoringStatus = useRef(scoringStatus);
  useEffect(() => {
    const wasInFlight =
      previousScoringStatus.current === 'pending' ||
      previousScoringStatus.current === 'in_progress';
    previousScoringStatus.current = scoringStatus;
    if (scoringStatus === 'complete' || scoringStatus === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      if (wasInFlight && msisdn) {
        invalidateEligibilityAssessment(queryClient, msisdn);
      }
    }
  }, [scoringStatus, customerId, msisdn, queryClient]);

  // Seeding the poll query with the returned status is what restarts the
  // timer above; the customer row is reread when it lands on complete/failed.
  const rescore = useMutation({
    mutationFn: () => rescoreCustomer(customerId),
    onSuccess: (result) => {
      if (!result.succeeded) {
        toast({
          title: 'Could not start re-scoring',
          description:
            result.errors?.[0] ??
            'The scoring service did not accept the request.',
          variant: 'destructive',
        });
        return;
      }
      queryClient.setQueryData(['customer-scoring-status', customerId], {
        ...result,
        value: { status: result.value?.status ?? 'pending' },
      });
      if (msisdn) invalidateEligibilityAssessment(queryClient, msisdn);
      toast({
        title: 'Re-scoring started',
        description:
          'Every signal source is fetched again. The score updates here when it lands.',
      });
    },
    onError: () =>
      toast({
        title: 'Could not start re-scoring',
        description: 'The request did not reach the server.',
        variant: 'destructive',
      }),
  });

  const customer = data?.value;

  /* ── Live eligibility → monetary credit amount ─────────── */
  // `availableCredit` isn't stored on the customer row, so fetch it from
  // the eligibility chain (loan-service → cs-engine). Only meaningful once
  // the customer is scored, so gate on a present score to avoid an extra
  // round trip for unscored customers.
  const { data: eligibilityResult, isLoading: eligibilityLoading } = useQuery({
    queryKey: ['customer-eligibility', customer?.phoneNumber],
    queryFn: () => checkCustomerEligibility(customer!.phoneNumber),
    enabled: !!customer?.phoneNumber && customer?.creditScore != null,
    staleTime: 60_000,
  });
  const availableCredit = eligibilityResult?.value?.availableCredit ?? null;
  const tierLadder = eligibilityResult?.value?.tiers;

  /* ── One financing at a time ──────────────────────────── */
  // A scored, creditworthy customer with a live contract still cannot start a
  // second application. Asking here means "Start Application" is disabled with
  // a reason on the page the agent is already looking at, rather than sending
  // them into a wizard that refuses them.
  const financingGate = useFinancingGate(customer?.id);
  const financingBlocker = financingGate.canApply
    ? null
    : financingGate.blocker;

  // Filter applications and contracts for this customer
  const customerApps = (appsData?.value?.items ?? appsData?.value ?? []).filter(
    (a: any) => a.customerId === customerId
  );
  const customerContracts = (contractsData?.value ?? []).filter(
    (c: any) => c.customerId === customerId || c.id === customerId
  );

  /* ── Loading state ──────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-48" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  /* ── Error state ────────────────────────────────────────── */
  if (error || !customer) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Customer not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/customers' })}
        >
          Back to Customers
        </Button>
      </div>
    );
  }

  const kycCfg = kycConfig[kycReviewState(customer)];
  // Anything not already verified is reviewable. The old `=== 'pending'` gate
  // deadlocked the loop the app introduced: we send a submission back, the
  // customer corrects it and resubmits, the row is still `rejected` — and the
  // only control that could act on it had disappeared.
  const canVerifyKyc =
    customer.kycStatus !== 'verified' &&
    hasPermission('customers', 'verifyKyc');
  const canRescore = hasPermission('customers', 'update');
  const awaitingReview =
    customer.kycStatus !== 'verified' && customer.kycSubmittedAt != null;

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/customers' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <CustomerAvatar url={customer.profileImage} />
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {customer.firstName} {customer.lastName}
              </h1>
              <p className="text-muted-foreground text-sm">
                {customer.phoneNumber}
                {customer.email ? ` · ${customer.email}` : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={cn('text-sm', kycCfg.className)}>
            {kycCfg.icon}
            KYC: {kycCfg.label}
          </Badge>
          <OdooLinkBadge
            subjectType="customer"
            subjectId={customerId}
            label={t('odoo.badge.partner')}
            showNotTracked
          />

          {(() => {
            const scoringInFlight =
              scoringStatus === 'pending' || scoringStatus === 'in_progress';
            const scoringComplete = scoringStatus === 'complete';
            const scoringFailed = scoringStatus === 'failed';

            // A verdict produced while a signal source was down is
            // indistinguishable from a real one, so re-scoring must be
            // reachable from the failed AND the complete state.
            const rescoreButton = canRescore &&
              (scoringComplete || scoringFailed) && (
                <Button
                  size="sm"
                  variant={scoringFailed ? 'default' : 'outline'}
                  onClick={() => rescore.mutate()}
                  disabled={rescore.isPending}
                  title={
                    scoringFailed
                      ? (scoringFailureReason ?? 'Scoring failed')
                      : 'Fetch every signal source again and re-score'
                  }
                  aria-label="Re-score"
                >
                  {rescore.isPending ? (
                    <ButtonSpinner className="mr-1" />
                  ) : (
                    <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  )}
                  {scoringFailed ? 'Scoring failed · Re-score' : 'Re-score'}
                </Button>
              );

            if (scoringFailed && rescoreButton) {
              return rescoreButton;
            }

            if (scoringComplete) {
              // Blocked by the one-financing rule: the button stays visible so
              // the layout doesn't shift, but says why. The full explanation
              // and the link to the blocking record sit below the header.
              if (financingBlocker) {
                return (
                  <>
                    {rescoreButton}
                    <Button
                      size="sm"
                      disabled
                      title={t('financingLock.stepsLockedHint')}
                      aria-label={t('financingLock.startBlocked')}
                    >
                      <Lock className="mr-1 h-3.5 w-3.5" />
                      {t('financingLock.startBlocked')}
                    </Button>
                  </>
                );
              }
              return (
                <>
                  {rescoreButton}
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate({
                        to: '/applications/new',
                        search: { customerId: customer.id },
                      })
                    }
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Start Application
                  </Button>
                </>
              );
            }

            const label = scoringInFlight
              ? 'Scoring in progress…'
              : scoringFailed
                ? 'Scoring failed'
                : 'Scoring not started';

            return (
              <Button
                size="sm"
                disabled
                title={scoringFailureReason ?? label}
                aria-label={label}
              >
                {scoringInFlight ? (
                  <ButtonSpinner className="mr-1" />
                ) : (
                  <Plus className="mr-1 h-3.5 w-3.5" />
                )}
                {label}
              </Button>
            );
          })()}

          {canVerifyKyc && (
            <Button
              size="sm"
              onClick={() => setKycDialogOpen(true)}
              className={cn(
                // A submission that is actually waiting on us gets the loud
                // treatment; recording a decision on a customer who never sent
                // anything is a rarer, quieter act.
                awaitingReview
                  ? 'bg-warning text-warning-foreground hover:bg-warning/90'
                  : ''
              )}
              variant={awaitingReview ? 'default' : 'outline'}
            >
              <ShieldCheck className="mr-1 h-3.5 w-3.5" />
              {awaitingReview ? 'Review identity' : 'Record ID decision'}
            </Button>
          )}
        </div>
      </div>

      {/* ── One financing at a time ───────────────────────── */}
      {financingBlocker && (
        <FinancingBlockInline
          blocker={financingBlocker}
          customerName={`${customer.firstName} ${customer.lastName}`.trim()}
        />
      )}

      {/* ── Stats Row ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard
          label="Credit Score"
          value={
            customer.creditScore != null ? String(customer.creditScore) : '-'
          }
          icon={TrendingUp}
          accent={customer.creditScore != null ? 'text-primary' : undefined}
        />
        <StatCard
          label="Credit Amount"
          value={
            eligibilityLoading && customer.creditScore != null ? (
              <Skeleton className="h-6 w-20" />
            ) : availableCredit != null ? (
              formatCurrency(availableCredit, currency)
            ) : (
              '-'
            )
          }
          icon={Wallet}
          accent={availableCredit != null ? 'text-primary' : undefined}
        />
        <StatCard
          label="Credit Tier"
          value={
            customer.creditTier ? (
              <CreditTierIndicator
                tier={customer.creditTier}
                tiers={tierLadder}
                size="sm"
              />
            ) : (
              '-'
            )
          }
          icon={CreditCard}
        />
        <StatCard
          label="Active Contracts"
          value={String(customer.activeContracts)}
          icon={FileText}
        />
        <StatCard
          label="Total Devices"
          value={String(customer.totalDevicesFinanced)}
          icon={Smartphone}
        />
      </div>

      {/* ── Main Layout ───────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Details + Tables */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary/80 to-primary text-white">
                  <User className="h-3.5 w-3.5" />
                </div>
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                <InfoRow
                  icon={<User className="h-3.5 w-3.5" />}
                  label="Full Name"
                  value={`${customer.firstName} ${customer.lastName}`}
                />
                <InfoRow
                  icon={<Phone className="h-3.5 w-3.5" />}
                  label="Phone"
                  value={customer.phoneNumber}
                />
                <InfoRow
                  icon={<Mail className="h-3.5 w-3.5" />}
                  label="Email"
                  value={customer.email ?? '-'}
                />
                <InfoRow
                  icon={<Hash className="h-3.5 w-3.5" />}
                  label="National ID"
                  value={customer.nationalId ?? '-'}
                />
                <InfoRow
                  icon={<Calendar className="h-3.5 w-3.5" />}
                  label="Date of Birth"
                  value={
                    customer.dateOfBirth
                      ? formatDate(customer.dateOfBirth)
                      : '-'
                  }
                />
                <InfoRow
                  icon={<Users className="h-3.5 w-3.5" />}
                  label="Gender"
                  value={
                    customer.gender
                      ? customer.gender.charAt(0).toUpperCase() +
                        customer.gender.slice(1)
                      : '-'
                  }
                />
                <InfoRow
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="Address"
                  value={customer.address ?? '-'}
                />
                <InfoRow
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="City / Region"
                  value={
                    [customer.city, customer.region]
                      .filter(Boolean)
                      .join(', ') || '-'
                  }
                />
                {/* Where the customer was signed up. Self-service channels
                    (USSD, mobile app) have no branch, hence the dash. */}
                <InfoRow
                  icon={<Building className="h-3.5 w-3.5" />}
                  label="Branch"
                  value={customer.branchName ?? '-'}
                />
              </div>
            </CardContent>
          </Card>

          {/* Eligibility Assessment — the scorecard grouped by source
              platform (ZAAD, Telco CDR, Mazeed Pay, Salaam Bank). */}
          <CustomerEligibilityAssessment
            msisdn={customer.phoneNumber}
            kycStatus={customer.kycStatus}
            kycVerifiedBy={customer.kycVerifiedBy ?? null}
            creditScore={customer.creditScore}
            creditTier={customer.creditTier}
            scoringStatus={scoringStatus}
            activeContracts={customer.activeContracts}
            totalDevicesFinanced={customer.totalDevicesFinanced}
          />

          {/* Applications Table */}
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <FileText className="h-3.5 w-3.5" />
                  </div>
                  Applications
                  {customerApps.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {customerApps.length}
                    </Badge>
                  )}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {customerApps.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Device</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerApps.map((app: any) => (
                      <TableRow
                        key={app.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          navigate({
                            to: '/applications/$applicationId',
                            params: { applicationId: app.id },
                          })
                        }
                      >
                        <TableCell className="font-medium">
                          {app.deviceBrand
                            ? `${app.deviceBrand} ${app.deviceModel ?? ''}`
                            : 'Device'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {app.planName ?? '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(() => {
                            // The installment as the contract will collect it —
                            // `monthlyInstallment` is a monthly reading of the
                            // term and misstates a daily or weekly plan.
                            const schedule = installmentSummary(app);
                            return schedule
                              ? `${formatCurrency(schedule.amount, currency)}${INSTALLMENT_PERIOD_SUFFIX[schedule.frequency]}`
                              : formatCurrency(app.financedAmount, currency);
                          })()}
                        </TableCell>
                        <TableCell>{appStatusBadge(app.status)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(app.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No applications found for this customer
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contracts Table */}
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
                    <CreditCard className="h-3.5 w-3.5" />
                  </div>
                  Contracts
                  {customerContracts.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {customerContracts.length}
                    </Badge>
                  )}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {customerContracts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Device</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Monthly</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Start Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerContracts.map((c: any) => (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          navigate({
                            to: '/contracts/$contractId',
                            params: { contractId: c.id },
                          })
                        }
                      >
                        <TableCell className="font-medium">
                          {c.deviceName ?? 'Device'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(c.totalAmount, currency)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatCurrency(c.monthlyPayment, currency)}
                        </TableCell>
                        <TableCell>{contractStatusBadge(c.status)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {c.startDate
                            ? formatDate(c.startDate)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No contracts found for this customer
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Identity — the submission AND the way to decide it. See
              `KycReviewCard`: a status with no evidence behind it is not
              something a reviewer can act on. */}
          <KycReviewCard
            customer={customer}
            canReview={canVerifyKyc}
            onReview={() => setKycDialogOpen(true)}
          />

          {/* Credit Assessment Card */}
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 text-white">
                  <TrendingUp className="h-3.5 w-3.5" />
                </div>
                Credit Assessment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customer.creditScore != null ? (
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <p className="text-4xl font-bold tracking-tight text-primary">
                      {customer.creditScore}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Credit Score
                    </p>
                  </div>

                  {customer.creditTier && (
                    <div className="flex items-center justify-center">
                      <CreditTierIndicator
                        tier={customer.creditTier}
                        tiers={tierLadder}
                        size="lg"
                      />
                    </div>
                  )}

                  {availableCredit != null ? (
                    <p className="text-center text-sm">
                      <span className="text-muted-foreground">
                        Available Credit:{' '}
                      </span>
                      <span className="font-semibold text-foreground">
                        {formatCurrency(availableCredit, currency)}
                      </span>
                    </p>
                  ) : eligibilityLoading ? (
                    <p className="text-center text-xs text-muted-foreground">
                      Calculating available credit…
                    </p>
                  ) : null}

                  {/* Score bar visualization */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>300</span>
                      <span>850</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 transition-all duration-500"
                        style={{
                          width: `${Math.min(100, Math.max(0, ((customer.creditScore - 300) / 550) * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : scoringStatus === 'pending' ||
                scoringStatus === 'in_progress' ? (
                <div className="py-6 text-center text-muted-foreground">
                  <SectionLoading text="Scoring in progress" />
                  <p className="text-xs mt-1">
                    Credit assessment runs automatically — usually completes
                    within a few seconds.
                  </p>
                </div>
              ) : scoringStatus === 'failed' ? (
                <div className="py-6 text-center text-muted-foreground">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-red-500/70" />
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">
                    Scoring failed
                  </p>
                  {scoringFailureReason && (
                    <p className="text-xs mt-1">{scoringFailureReason}</p>
                  )}
                </div>
              ) : (
                <div className="py-6 text-center text-muted-foreground">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No credit score available</p>
                  <p className="text-xs mt-1">
                    Score is calculated from telco CBS metrics
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* System Info */}
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-gray-500 to-gray-600 text-white">
                  <Hash className="h-3.5 w-3.5" />
                </div>
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-1">
                <span className="text-sm text-muted-foreground">
                  Customer ID
                </span>
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded max-w-[180px] truncate">
                  {customer.id}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-sm text-muted-foreground">Joined</span>
                <span className="text-sm font-medium">
                  {formatDate(customer.createdAt)}
                </span>
              </div>
              {customer.updatedAt && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-sm text-muted-foreground">
                    Last Updated
                  </span>
                  <span className="text-sm font-medium">
                    {formatDate(customer.updatedAt)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Every channel this customer used, in one chronological list:
              in-app support tickets AND the feedback they submitted from the
              financed handset. This replaces the tickets-only panel that used
              to sit here — rendering both would show every ticket twice, and
              the point of the union is that an agent calling this customer
              back does not have to know which channel they reached us on. */}
          <ContactHistoryPanel customerId={customer.id} />
        </div>
      </div>

      {/* One dialog for both entry points — the header action and the identity
          card in the sidebar. It owns the verdict form and the mutation; this
          page owns only whether it is open. */}
      <KycReviewDialog
        open={kycDialogOpen}
        onOpenChange={setKycDialogOpen}
        customer={customer}
      />
    </div>
  );
}

/* ── Reusable Components ────────────────────────────────────── */

/** The sign-up selfie when there is one; opens full size. */
function CustomerAvatar({ url }: Readonly<{ url: string | null }>) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const well =
    'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl shadow-lg';

  if (!url || failedUrl === url) {
    return (
      <div className={cn(well, 'bg-gradient-to-br from-primary/80 to-primary text-white')}>
        <User className="h-5 w-5" />
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open selfie full size"
      className={cn(well, 'ring-2 ring-primary/60 ring-offset-2 ring-offset-background')}
    >
      <img
        src={url}
        alt="Customer selfie"
        onError={() => setFailedUrl(url)}
        className="h-full w-full object-cover"
      />
    </a>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
        {icon && <span className="text-muted-foreground/60">{icon}</span>}
        {label}
      </span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  );
}

/**
 * Credit-tier display that opens a small popover with the tier's score range
 * and a brief explanation. Click-based so it works on touch devices too.
 */
function CreditTierIndicator({
  tier,
  tiers,
  size = 'sm',
}: {
  tier: string;
  tiers: readonly CreditTierInfoModel[] | undefined;
  size?: 'sm' | 'lg';
}) {
  const colorClass = tierColors[tier] ?? '';
  const label = tierName(tier, tiers);
  const scoreRange = tierScoreRange(tier, tiers);
  const description = tierDescriptions[tier];

  const isLarge = size === 'lg';
  const circleClass = isLarge
    ? 'inline-flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-bold'
    : 'inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-bold';
  const labelClass = isLarge
    ? 'text-sm font-medium'
    : 'text-muted-foreground text-xs';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Credit tier ${tier} ${label} — view details`}
          className={cn(
            'group inline-flex items-center gap-1.5 rounded-md transition-opacity',
            'hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2'
          )}
        >
          <span className={cn(circleClass, colorClass)}>{tier}</span>
          <span className={labelClass}>
            {isLarge ? `Tier ${tier} · ${label}` : label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-full border-2 text-base font-bold',
                colorClass
              )}
            >
              {tier}
            </span>
            <div>
              <p className="text-sm font-semibold">Tier {tier}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </div>
          {scoreRange && (
            <div className="rounded-md bg-muted/40 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Score range
              </p>
              <p className="text-sm font-medium tabular-nums">{scoreRange}</p>
            </div>
          )}
          {description && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ElementType;
  accent?: string;
}) {
  return (
    <Card className="shadow-md border-0">
      <CardContent className="pt-5 pb-4 px-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{label}</p>
            <div className={cn('text-xl font-bold mt-0.5', accent)}>
              {value}
            </div>
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
